import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  DeleteOutline as DeleteOutlineIcon,
  InsertDriveFile as InsertDriveFileIcon,
  MoreVert as MoreVertIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import {
  helpApi,
  type HelpRequest,
  type HelpRequestMessage,
} from '@/api/help'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { formatDateTimeDMY } from '@/utils/date'
import '@/styles/dashboard.css'

const HELP_ATTACHMENT_MAX_COUNT = 5
const HELP_ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024
const HELP_ATTACHMENT_ACCEPT =
  'image/jpeg,image/png,image/webp,image/gif,application/pdf,.jpg,.jpeg,.png,.webp,.gif,.pdf'
const HELP_ATTACHMENT_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
])
const HELP_ATTACHMENT_EXT = new Set(['jpg', 'jpeg', 'png', 'webp', 'gif', 'pdf'])

const createHelpSchema = (t: (key: string) => string) =>
  z.object({
    title: z
      .string()
      .trim()
      .min(1, t('help.validation.titleRequired'))
      .max(255, t('help.validation.titleMax')),
    body: z
      .string()
      .trim()
      .min(1, t('help.validation.bodyRequired'))
      .max(10000, t('help.validation.bodyMax')),
  })

function formatFileSize(bytes: number, t: (key: string, options?: Record<string, unknown>) => string): string {
  if (bytes < 1024) return t('help.attachments.sizeBytes', { size: bytes })
  if (bytes < 1024 * 1024) {
    return t('help.attachments.sizeKb', { size: (bytes / 1024).toFixed(1) })
  }
  return t('help.attachments.sizeMb', { size: (bytes / (1024 * 1024)).toFixed(1) })
}

function isAllowedHelpFile(file: File): boolean {
  if (HELP_ATTACHMENT_MIME.has(file.type)) return true
  const ext = file.name.split('.').pop()?.toLowerCase() ?? ''
  return HELP_ATTACHMENT_EXT.has(ext)
}

/**
 * Страница «Помощь»: форма обращения + история (список и деталь) + вложения.
 * Отправка через POST /api/help; история — GET /api/help и GET /api/help/{id}.
 */
export function HelpPage() {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const fileInputRef = useRef<HTMLInputElement>(null)
  const detailFileInputRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [pendingFiles, setPendingFiles] = useState<File[]>([])
  const [attachmentsError, setAttachmentsError] = useState<string | null>(null)
  const [uploadPercent, setUploadPercent] = useState<number | null>(null)

  const [items, setItems] = useState<HelpRequest[]>([])
  const [listLoading, setListLoading] = useState(true)
  const [listError, setListError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(15)
  const [total, setTotal] = useState(0)

  const [detailOpen, setDetailOpen] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)
  const [detail, setDetail] = useState<HelpRequest | null>(null)
  const [detailUploadError, setDetailUploadError] = useState<string | null>(null)
  const [detailUploading, setDetailUploading] = useState(false)
  const [detailUploadPercent, setDetailUploadPercent] = useState<number | null>(null)

  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null)
  const [actionsItem, setActionsItem] = useState<HelpRequest | null>(null)

  const helpSchema = createHelpSchema(t)
  type HelpFormData = z.infer<typeof helpSchema>

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<HelpFormData>({
    resolver: zodResolver(helpSchema),
    defaultValues: {
      title: '',
      body: '',
    },
  })

  const loadList = useCallback(
    async (params: { page: number; per_page: number }) => {
      try {
        setListError(null)
        setListLoading(true)
        const result = await helpApi.index({ page: params.page, per_page: params.per_page })
        setItems(result.data)
        setPage(result.current_page)
        setPerPage(result.per_page)
        setTotal(result.total)
      } catch (err: unknown) {
        setListError(displayErrorMessage(getErrorMessage(err), t('help.history.loadError')))
      } finally {
        setListLoading(false)
      }
    },
    [t],
  )

  useEffect(() => {
    loadList({ page: 1, per_page: 15 }).catch(() => {})
  }, [loadList])

  const validateIncomingFiles = (
    incoming: File[],
    currentCount: number,
  ): { accepted: File[]; error: string | null } => {
    const accepted: File[] = []
    let errorMsg: string | null = null

    for (const file of incoming) {
      if (currentCount + accepted.length >= HELP_ATTACHMENT_MAX_COUNT) {
        errorMsg = t('help.attachments.maxCount', { count: HELP_ATTACHMENT_MAX_COUNT })
        break
      }
      if (!isAllowedHelpFile(file)) {
        errorMsg = t('help.attachments.invalidType')
        continue
      }
      if (file.size > HELP_ATTACHMENT_MAX_BYTES) {
        errorMsg = t('help.attachments.maxSize', {
          size: t('help.attachments.sizeMb', { size: 5 }),
        })
        continue
      }
      accepted.push(file)
    }

    return { accepted, error: errorMsg }
  }

  const handlePendingFilesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (selected.length === 0) return

    const { accepted, error: validationError } = validateIncomingFiles(
      selected,
      pendingFiles.length,
    )
    setAttachmentsError(validationError)
    if (accepted.length > 0) {
      setPendingFiles((prev) => [...prev, ...accepted].slice(0, HELP_ATTACHMENT_MAX_COUNT))
    }
  }

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index))
    setAttachmentsError(null)
  }

  const openDetail = async (id: number) => {
    setDetailOpen(true)
    setDetailLoading(true)
    setDetailError(null)
    setDetailUploadError(null)
    setDetail(null)
    try {
      const item = await helpApi.show(id)
      setDetail(item)
    } catch (err: unknown) {
      setDetailError(displayErrorMessage(getErrorMessage(err), t('help.history.detailError')))
    } finally {
      setDetailLoading(false)
    }
  }

  const closeDetail = () => {
    setDetailOpen(false)
    setDetailLoading(false)
    setDetailError(null)
    setDetailUploadError(null)
    setDetailUploading(false)
    setDetailUploadPercent(null)
    setDetail(null)
  }

  const closeActionsMenu = () => {
    setActionsAnchorEl(null)
    setActionsItem(null)
  }

  const statusLabel = (status: HelpRequest['status']) => {
    switch (status) {
      case 'submitted':
        return t('help.status.submitted')
      case 'pending':
        return t('help.status.pending')
      case 'accepted':
        return t('help.status.accepted')
      case 'declined':
        return t('help.status.declined')
      case 'snoozed':
        return t('help.status.snoozed')
      case 'duplicate':
        return t('help.status.duplicate')
      default:
        return t('help.status.unknown')
    }
  }

  const statusColor = (
    status: HelpRequest['status'],
  ): 'default' | 'info' | 'success' | 'warning' | 'error' => {
    switch (status) {
      case 'submitted':
        return 'info'
      case 'pending':
        return 'warning'
      case 'accepted':
        return 'success'
      case 'declined':
        return 'error'
      case 'snoozed':
        return 'warning'
      case 'duplicate':
        return 'default'
      default:
        return 'default'
    }
  }

  const messageAuthorLabel = (message: HelpRequestMessage) => {
    if (message.author_name?.trim()) return message.author_name.trim()
    if (message.source === 'system') return t('help.history.messageAuthorSystem')
    return t('help.history.messageAuthorSupport')
  }

  const messageTimestamp = (message: HelpRequestMessage) =>
    formatDateTimeDMY(message.plane_created_at ?? message.created_at)

  const onSubmit = async (data: HelpFormData) => {
    try {
      setError(null)
      setSuccess(null)
      setAttachmentsError(null)
      setIsSubmitting(true)
      setUploadPercent(pendingFiles.length > 0 ? 0 : null)
      await helpApi.create(
        {
          title: data.title,
          body: data.body,
          attachments: pendingFiles.length > 0 ? pendingFiles : undefined,
        },
        pendingFiles.length > 0
          ? (progress) => setUploadPercent(progress.percent)
          : undefined,
      )
      setSuccess(t('help.success'))
      reset()
      setPendingFiles([])
      setUploadPercent(null)
      await loadList({ page: 1, per_page: perPage })
    } catch (err: unknown) {
      setError(displayErrorMessage(getErrorMessage(err), t('help.error')))
      setUploadPercent(null)
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDetailFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (!detail || selected.length === 0) return

    const currentCount = detail.attachments?.length ?? 0
    const { accepted, error: validationError } = validateIncomingFiles(selected, currentCount)
    if (validationError) {
      setDetailUploadError(validationError)
    } else {
      setDetailUploadError(null)
    }
    if (accepted.length === 0) return

    setDetailUploading(true)
    setDetailUploadPercent(0)
    try {
      let latest = detail
      for (const file of accepted) {
        const attachment = await helpApi.uploadAttachment(detail.id, file, (progress) => {
          setDetailUploadPercent(progress.percent)
        })
        latest = {
          ...latest,
          attachments: [...(latest.attachments ?? []), attachment],
        }
        setDetail(latest)
      }
      setDetailUploadPercent(null)
    } catch (err: unknown) {
      setDetailUploadError(
        displayErrorMessage(getErrorMessage(err), t('help.attachments.uploadError')),
      )
      setDetailUploadPercent(null)
    } finally {
      setDetailUploading(false)
    }
  }

  const handlePageChange = (_: unknown, newPage: number) => {
    loadList({ page: newPage + 1, per_page: perPage }).catch(() => {})
  }

  const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = parseInt(e.target.value, 10)
    loadList({ page: 1, per_page: next }).catch(() => {})
  }

  const renderHistorySkeleton = () =>
    isMobile ? (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[1, 2, 3].map((i) => (
          <Paper key={i} sx={{ p: 2 }}>
            <Skeleton variant="text" width="70%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="rectangular" width={90} height={24} sx={{ borderRadius: 1 }} />
          </Paper>
        ))}
      </Box>
    ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('help.history.colTitle')}</TableCell>
              <TableCell>{t('help.history.colStatus')}</TableCell>
              <TableCell>{t('help.history.colCreated')}</TableCell>
              <TableCell align="right">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell>
                  <Skeleton variant="text" width="70%" />
                </TableCell>
                <TableCell>
                  <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
                </TableCell>
                <TableCell>
                  <Skeleton variant="text" width={120} />
                </TableCell>
                <TableCell align="right">
                  <Skeleton variant="circular" width={32} height={32} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )

  // Карточка грузит связь, список — нет: считаем по тому, что пришло.
  const detailAttachmentCount = detail?.attachments?.length ?? detail?.attachments_count ?? 0
  const canAddDetailAttachment =
    !!detail && detailAttachmentCount < HELP_ATTACHMENT_MAX_COUNT && !detailUploading

  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="h4" component="h1" fontWeight={700}>
            {t('help.title')}
          </Typography>
          <PageHelpPopover pageKey="help" />
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {t('help.subtitle')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('help.formTitle')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('help.formHint')}
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit(onSubmit)}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}
            >
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('help.fieldTitle')}
                    fullWidth
                    size="small"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={isSubmitting}
                  />
                )}
              />
              <Controller
                name="body"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t('help.fieldBody')}
                    fullWidth
                    size="small"
                    multiline
                    minRows={6}
                    error={!!errors.body}
                    helperText={errors.body?.message}
                    disabled={isSubmitting}
                  />
                )}
              />

              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t('help.attachments.label')}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  {t('help.attachments.hint')}
                </Typography>
                {attachmentsError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 1.5 }}
                    onClose={() => setAttachmentsError(null)}
                  >
                    {attachmentsError}
                  </Alert>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  component="label"
                  startIcon={<AttachFileIcon />}
                  disabled={isSubmitting || pendingFiles.length >= HELP_ATTACHMENT_MAX_COUNT}
                  sx={{ height: 40, textTransform: 'none' }}
                >
                  {t('help.attachments.choose')}
                  <input
                    ref={fileInputRef}
                    type="file"
                    hidden
                    multiple
                    accept={HELP_ATTACHMENT_ACCEPT}
                    onChange={handlePendingFilesChange}
                  />
                </Button>
                {pendingFiles.length > 0 && (
                  <List dense disablePadding sx={{ mt: 1 }}>
                    {pendingFiles.map((file, index) => (
                      <ListItem
                        key={`${file.name}-${file.size}-${index}`}
                        sx={{ px: 0 }}
                        secondaryAction={
                          <IconButton
                            edge="end"
                            size="small"
                            aria-label={t('help.attachments.remove')}
                            disabled={isSubmitting}
                            onClick={() => removePendingFile(index)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        }
                      >
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <InsertDriveFileIcon fontSize="small" color="action" />
                        </ListItemIcon>
                        <ListItemText
                          primary={file.name}
                          secondary={formatFileSize(file.size, t)}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
                {uploadPercent !== null && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {t('help.attachments.uploading', { percent: uploadPercent })}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={uploadPercent}
                      sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                    />
                  </Box>
                )}
              </Box>

              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  size="small"
                  disabled={isSubmitting}
                  sx={{ height: 40 }}
                >
                  {isSubmitting ? t('help.submitting') : t('help.submit')}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="h6" gutterBottom>
          {t('help.history.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('help.history.subtitle')}
        </Typography>

        {listError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setListError(null)}>
            {listError}
          </Alert>
        )}

        {listLoading ? (
          renderHistorySkeleton()
        ) : items.length === 0 ? (
          <Paper>
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('help.history.empty')}
              </Typography>
            </Box>
          </Paper>
        ) : isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item) => (
              <Paper
                key={item.id}
                sx={{ p: 2, cursor: 'pointer' }}
                onClick={() => {
                  openDetail(item.id).catch(() => {})
                }}
              >
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 0.5 }}>
                  {item.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {formatDateTimeDMY(item.created_at)}
                </Typography>
                <Chip
                  size="small"
                  label={statusLabel(item.status)}
                  color={statusColor(item.status)}
                />
              </Paper>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t('help.history.colTitle')}</TableCell>
                  <TableCell>{t('help.history.colStatus')}</TableCell>
                  <TableCell>{t('help.history.colCreated')}</TableCell>
                  <TableCell align="right">{t('common.actions')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow
                    key={item.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => {
                      openDetail(item.id).catch(() => {})
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.title}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={statusLabel(item.status)}
                        color={statusColor(item.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDateTimeDMY(item.created_at)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        aria-label={t('common.actions')}
                        onClick={(e) => {
                          setActionsAnchorEl(e.currentTarget)
                          setActionsItem(item)
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {total > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'flex-end',
              mt: 2,
              flex: 1,
              minWidth: 0,
              overflow: 'auto',
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={Math.max(0, page - 1)}
              onPageChange={handlePageChange}
              rowsPerPage={perPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage={isMobile ? '' : t('help.history.rowsPerPage')}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}–${to} ${t('help.history.of')} ${
                  count !== -1 ? count : `${t('help.history.moreThan')} ${to}`
                }`
              }
              sx={{
                overflow: 'hidden',
                '& .MuiTablePagination-toolbar': { flexWrap: 'wrap', rowGap: 1 },
              }}
            />
          </Box>
        )}

        <Menu
          anchorEl={actionsAnchorEl}
          open={Boolean(actionsAnchorEl)}
          onClose={closeActionsMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <MenuItem
            onClick={() => {
              if (actionsItem) {
                openDetail(actionsItem.id).catch(() => {})
                closeActionsMenu()
              }
            }}
          >
            <VisibilityIcon sx={{ mr: 1 }} fontSize="small" />
            {t('help.history.view')}
          </MenuItem>
        </Menu>

        <Dialog open={detailOpen} onClose={closeDetail} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', pr: 1 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {detailLoading ? (
                <Skeleton variant="text" width="60%" height={28} />
              ) : (
                detail?.title ?? t('help.history.detailTitle')
              )}
            </Box>
            <IconButton aria-label={t('common.close')} onClick={closeDetail} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {detailLoading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                <Skeleton variant="rounded" height={24} width={100} />
                <Skeleton variant="text" width="40%" />
                <Skeleton variant="rounded" height={120} />
                <Skeleton variant="text" width="30%" />
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="rounded" height={72} />
              </Box>
            ) : detailError ? (
              <Alert severity="error">{detailError}</Alert>
            ) : detail ? (
              <Box>
                <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                  <Chip
                    size="small"
                    label={statusLabel(detail.status)}
                    color={statusColor(detail.status)}
                  />
                  <Typography variant="body2" color="text.secondary">
                    {formatDateTimeDMY(detail.created_at)}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('help.history.fieldTitle')}
                  </Typography>
                  <Typography variant="body1">{detail.title}</Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('help.history.fieldBody')}
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {detail.body}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('help.attachments.title')}
                  </Typography>
                  {detailUploadError && (
                    <Alert
                      severity="error"
                      sx={{ mb: 1.5 }}
                      onClose={() => setDetailUploadError(null)}
                    >
                      {detailUploadError}
                    </Alert>
                  )}
                  {detailAttachmentCount === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('help.attachments.empty')}
                    </Typography>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ mb: 0.5 }}>
                        {t('help.attachments.count', { count: detailAttachmentCount })}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 1 }}>
                        {t('help.attachments.storedInSupport')}
                      </Typography>
                    </>
                  )}
                  {canAddDetailAttachment && (
                    <Button
                      variant="outlined"
                      size="small"
                      component="label"
                      startIcon={<AttachFileIcon />}
                      sx={{ height: 40, mt: 1, textTransform: 'none' }}
                    >
                      {t('help.attachments.add')}
                      <input
                        ref={detailFileInputRef}
                        type="file"
                        hidden
                        multiple
                        accept={HELP_ATTACHMENT_ACCEPT}
                        onChange={(e) => {
                          handleDetailFileChange(e).catch(() => {})
                        }}
                      />
                    </Button>
                  )}
                  {detailUploadPercent !== null && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {t('help.attachments.uploading', { percent: detailUploadPercent })}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={detailUploadPercent}
                        sx={{ mt: 0.5, height: 6, borderRadius: 1 }}
                      />
                    </Box>
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                  {t('help.history.messagesTitle')}
                </Typography>
                {(detail.messages ?? []).length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    {t('help.history.messagesEmpty')}
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(detail.messages ?? []).map((message) => (
                      <Paper
                        key={message.id}
                        variant="outlined"
                        sx={{ p: 1.5, bgcolor: 'action.hover' }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignItems: 'baseline',
                            mb: 0.75,
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {messageAuthorLabel(message)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {messageTimestamp(message)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {message.body}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            ) : null}
          </DialogContent>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
